import {
  TInventoryReportPdfReq,
  TInventoryReportPreviewReq,
} from '@models/inventoryReport';
import { TGetMasterDataStoreRes } from '@models/master';
import { inventoryReportApi } from '@services/inventoryReportApi';
import { inventorySessionApi } from '@services/inventorySessionApi';
import { masterDataApi } from '@services/masterDataApi';
import { TPeriodOption, TReportPreview } from '../constant';

/**
 * API layer THẬT cho VAMS_PC_007 — nối theo `vams_docs/Tasks/PC007/api-integration.md`.
 *
 * ĐÃ NỐI API THẬT (contract §6 — vams-fe-api-integrator):
 *   - GET  /api/v1/reports/inventory/preview → getPreview()   (contract §2.1, ĐÃ CÓ ở BE)
 *   - POST /api/v1/reports/inventory/pdf     → generatePdf()  (contract §2.2, ĐÃ CÓ ở BE, responseType:'blob')
 *
 * CÒN MOCK — chờ BE bổ sung (Gap G1, contract §2.3/§2.4):
 *   - Dropdown [4] 棚卸年月 (MOCK_PERIODS) và [5] 店舗 (MOCK_STORES) — khai báo/dùng trực tiếp trong
 *     `../constant.ts` và `useInventoryReport.tsx`, KHÔNG đụng tới ở file này. Không tự bịa endpoint
 *     lookup cho tới khi BE chốt hướng xử lý Gap G1.
 */
function useInventoryReportApi() {
  /** GET .../preview — [6.2] reload mỗi khi [4]/[5] đổi giá trị. */
  const getPreview = async (
    inventoryYearMonth: string,
    storeCode: string,
    companyCode?: string,
    page = 1
  ): Promise<TReportPreview> => {
    const payload: TInventoryReportPreviewReq = {
      inventoryYearMonth,
      storeCode,
      companyCode,
      page,
    };
    const response = await inventoryReportApi.getPreview(payload);
    return response.data;
  };

  /** POST .../pdf — [D5] OK, trả Blob PDF nhị phân trực tiếp (S01) khi thành công. */
  const generatePdf = async (
    inventoryYearMonth: string,
    storeCode: string,
    companyCode?: string
  ): Promise<Blob> => {
    const payload: TInventoryReportPdfReq = {
      inventoryYearMonth,
      storeCode,
      companyCode,
    };
    return inventoryReportApi.generatePdf(payload);
  };

  /** GET /api/v1/inventory/sessions — danh sách kỳ 棚卸年月 cho dropdown [4], sort desc, dedupe theo inventoryYearMonth. */
  const getSessions = async (): Promise<TPeriodOption[]> => {
    const response = await inventorySessionApi.getSessions();
    if (!response.ok) {
      return [];
    }

    const currentYear = new Date().getFullYear();
    const minYear = currentYear - 6; // 7 năm gần nhất (cùng giới hạn với PC004)

    const seen = new Set<string>();
    const options: TPeriodOption[] = [];
    const sorted = [...response.data].sort((a, b) =>
      (b.inventoryYearMonth ?? '').localeCompare(a.inventoryYearMonth ?? '')
    );
    for (const item of sorted) {
      const ym = item.inventoryYearMonth;
      if (!ym || seen.has(ym)) {
        continue;
      }
      if (item.inventoryYear !== undefined && item.inventoryYear < minYear) {
        continue;
      }
      seen.add(ym);
      options.push({
        sessionCode: item.sessionCode,
        inventoryYearMonth: ym,
        inventoryYearMonthDisplay: `${ym.slice(0, 4)}/${ym.slice(4)}`,
        statusCode:
          (item.statusCode as TPeriodOption['statusCode']) ?? 'CLOSED',
      });
    }
    return options;
  };

  /** GET /api/v1/store — danh sách toàn bộ store, dùng cho dropdown [5] khi role=ADMIN (không lọc theo companyCode). */
  const getStores = async (): Promise<TGetMasterDataStoreRes[]> => {
    const response = await masterDataApi.getStore({ companyId: '' });
    return response.data;
  };

  return {
    handler: {
      getSessions,
      getPreview,
      generatePdf,
      getStores,
    },
  };
}

export default useInventoryReportApi;
